import { NgIf, NgFor, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { Component, Inject, HostListener, ElementRef, ViewChild } from '@angular/core';
import { MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-preview-dialog',
  standalone: true,
  imports: [MatDialogModule, NgIf, NgFor, NgSwitch, NgSwitchCase, NgSwitchDefault, MatIconModule, MatButtonModule],
  templateUrl: './preview-dialog.component.html',
  styleUrls: ['./preview-dialog.component.scss']
})
export class PreviewDialogComponent {
  @ViewChild('imageRef') imageRef!: ElementRef<HTMLImageElement>;

  safeImageUrl: SafeUrl | undefined;
  zoomLevel = 100;
  rotation = 0;
  isDragging = false;
  position = { x: 0, y: 0 };
  dragStart = { x: 0, y: 0 };
  isFullscreen = false;
  isHtml = false; // New property to identify HTML content
  isPdfFile = false; // New property for PDF files
  isWordFile = false; // New property for Word files
  isExcelFile = false; // New property for Excel files

  storageUrl = environment.storageUrl;

  constructor(
    private sanitizer: DomSanitizer,
    @Inject(MAT_DIALOG_DATA) public data: { 
      url: string; 
      title: string; 
      filename?: string; 
      isHtml?: boolean; 
      isPdf?: boolean; 
      isWord?: boolean; 
      isExcel?: boolean;
      attachments?: string[];
      currentIndex?: number;
    }
  ) {
    console.log('PreviewDialogComponent: received data.url:', this.data.url);
    
    // Use the URL as-is if it starts with http, data:, or blob:, otherwise prepend storageUrl
    if (this.data.url && !this.data.url.startsWith('http') && !this.data.url.startsWith('data:') && !this.data.url.startsWith('blob:')) {
      this.data.url = `${environment.storageUrl}/${this.data.url}`;
    }
    
    this.safeImageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.data.url); // Changed to bypassSecurityTrustResourceUrl
    console.log('PreviewDialogComponent: final URL:', this.data.url);
    console.log('PreviewDialogComponent: safeImageUrl generated:', this.safeImageUrl);
    
    if (!this.data.filename) {
      this.data.filename = this.extractFilenameFromUrl(this.data.url);
    }
    
    this.isHtml = this.data.isHtml || this.data.url?.match(/\.(html|htm)$/i) !== null;
    this.isPdfFile = this.data.isPdf || this.data.url?.match(/\.pdf$/i) !== null; // Detect PDF files
    this.isWordFile = this.data.isWord || this.data.url?.match(/\.(doc|docx)$/i) !== null; // Detect Word files
    this.isExcelFile = this.data.isExcel || this.data.url?.match(/\.(xls|xlsx)$/i) !== null; // Detect Excel files
    
    console.log('PreviewDialogComponent: isHtml set to:', this.isHtml);
    console.log('PreviewDialogComponent: isPdfFile set to:', this.isPdfFile);
    console.log('PreviewDialogComponent: isWordFile set to:', this.isWordFile);
    console.log('PreviewDialogComponent: isExcelFile set to:', this.isExcelFile);
  }

  showAttachmentAtIndex(index: number): void {
    if (!this.data.attachments || index < 0 || index >= this.data.attachments.length) {
      return;
    }
    this.data.currentIndex = index;
    const url = this.data.attachments[index];
    
    let displayUrl = url;
    if (!displayUrl.startsWith('http')) {
      displayUrl = `${environment.storageUrl}/${displayUrl}`;
    }
    this.data.url = displayUrl;
    this.data.filename = this.extractFilenameFromUrl(displayUrl);
    this.data.title = this.data.filename;
    
    this.safeImageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.data.url);
    
    this.isHtml = this.data.url?.match(/\.(html|htm)$/i) !== null;
    this.isPdfFile = this.data.url?.match(/\.pdf$/i) !== null;
    this.isWordFile = this.data.url?.match(/\.(doc|docx)$/i) !== null;
    this.isExcelFile = this.data.url?.match(/\.(xls|xlsx)$/i) !== null;
    
    this.reset();
  }

  prevAttachment(): void {
    if (this.data.attachments && this.data.currentIndex !== undefined && this.data.currentIndex > 0) {
      this.showAttachmentAtIndex(this.data.currentIndex - 1);
    }
  }

  nextAttachment(): void {
    if (this.data.attachments && this.data.currentIndex !== undefined && this.data.currentIndex < this.data.attachments.length - 1) {
      this.showAttachmentAtIndex(this.data.currentIndex + 1);
    }
  }

  hasMultipleAttachments(): boolean {
    return !!(this.data.attachments && this.data.attachments.length > 1);
  }

  isImage(): boolean {
    return this.data.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i) !== null && !this.isHtml && !this.isPdfFile && !this.isWordFile && !this.isExcelFile;
  }

  isPdf(): boolean {
    return this.isPdfFile; // Now directly uses the flag set in the constructor
  }

  isWord(): boolean {
    return this.isWordFile; // Directly uses the flag
  }

  isExcel(): boolean {
    return this.isExcelFile; // Directly uses the flag
  }

  private extractFilenameFromUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      return pathname.substring(pathname.lastIndexOf('/') + 1) || 'document';
    } catch {
      return 'document';
    }
  }

  zoomIn(): void {
    this.zoomLevel = Math.min(this.zoomLevel + 25, 500);
  }

  zoomOut(): void {
    this.zoomLevel = Math.max(this.zoomLevel - 25, 25);
  }

  handleZoomChange(value: number): void {
    this.zoomLevel = value;
  }

  rotate(): void {
    this.rotation = (this.rotation + 90) % 360;
  }

  reset(): void {
    this.zoomLevel = 100;
    this.rotation = 0;
    this.position = { x: 0, y: 0 };
  }

  download(): void {
    if (!this.data.url) return;
    
    fetch(this.data.url)
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = this.data.filename || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(error => {
        console.error('Error downloading file via blob, falling back to direct link:', error);
        const link = document.createElement('a');
        link.href = this.data.url;
        link.target = '_blank';
        link.download = this.data.filename || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
  }

  handleMouseDown(e: MouseEvent): void {
    if (this.zoomLevel > 100) {
      this.isDragging = true;
      this.dragStart = {
        x: e.clientX - this.position.x,
        y: e.clientY - this.position.y,
      };
    }
  }

  handleMouseMove(e: MouseEvent): void {
    if (this.isDragging && this.zoomLevel > 100) {
      this.position = {
        x: e.clientX - this.dragStart.x,
        y: e.clientY - this.dragStart.y,
      };
    }
  }

  handleMouseUp(): void {
    this.isDragging = false;
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.isFullscreen = false;
  }

  getFileType(filePath: string): 'image' | 'pdf' | 'doc' | 'xls' | 'other' {
    if (!filePath) return 'other';
    const cleanPath = filePath.toLowerCase();
    if (cleanPath.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      return 'image';
    } else if (cleanPath.endsWith('.pdf')) {
      return 'pdf';
    } else if (cleanPath.match(/\.(doc|docx)$/i)) {
      return 'doc';
    } else if (cleanPath.match(/\.(xls|xlsx)$/i)) {
      return 'xls';
    }
    return 'other';
  }

  getAttachmentFullUrl(filePath: string): string {
    if (!filePath) return '';
    return filePath.startsWith('http') ? filePath : `${this.storageUrl}/${filePath}`;
  }
}
